import React, { forwardRef } from 'react';

import CheckSmall from '@rover/icons/svg/check-small.svg';
import { Flex, Text } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';

import { ThemeBackgroundColors } from '../../../styles/themeTypes';
import { throwOrLogError } from '../../../utils';

import { SelectFieldOptionConfig } from './SelectField.common';

export type { SelectFieldOptionConfig as Option };

type SelectFieldOptionProps = {
  id: string;
  key: string;
  option: SelectFieldOptionConfig;
  isSelected: boolean;
  onClick: (option: SelectFieldOptionConfig) => void;
};

const SelectFieldOption = forwardRef<HTMLElement, SelectFieldOptionProps>(
  ({ id, option, isSelected, onClick: handleOptionClick, ...other }, ref) => {
    const theme = useTheme();
    const { background, colors } = theme;
    const isDisabled = option.disabled;

    if (isSelected && isDisabled) {
      throwOrLogError('SelectField.props isSelected and isDisabled should be mutually exclusive');
    }

    let backgroundColor: ThemeBackgroundColors = 'interactive.primary_base';
    if (isDisabled) {
      backgroundColor = 'interactive.disabled';
    } else if (isSelected) {
      backgroundColor = 'interactive.button_primary_inverse_base';
    }

    return (
      <Flex
        {...other}
        id={id}
        aria-selected={isSelected}
        aria-disabled={isDisabled}
        htmlRef={ref}
        webProps={{
          tabIndex: -1,
          role: 'option',
        }}
        padding="3x"
        background={backgroundColor}
        onClick={isDisabled ? undefined : () => handleOptionClick(option)}
        sx={{
          userSelect: 'none',
          cursor: isDisabled ? 'not-allowed' : 'pointer',
          '&:focus': !isDisabled
            ? {
                'box-shadow': `inset 0 0 0 2px ${colors.border.input_focus}`,
              }
            : {},
          '&:focus-visible': {
            outline: 'none',
          },
          '@media (hover: hover)': {
            '&:hover': !isDisabled
              ? {
                  background: !isSelected
                    ? background.interactive.primary_pressed
                    : background.interactive.button_primary_inverse_pressed,
                }
              : {},
          },
        }}
      >
        <Flex flexGrow="1" mr="2x">
          <Text textColor={isDisabled ? 'disabled' : undefined}>{option.label}</Text>
        </Flex>
        <CheckSmall
          height="24px"
          width="24px"
          display={isSelected && !isDisabled ? 'block' : 'none'}
          fill={colors.interactive_text.primary_active}
        />
      </Flex>
    );
  }
);

export default SelectFieldOption;
